import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePostHog } from 'posthog-js/react';
import { KeyedMutator } from 'swr/_internal';
import { Button, Grid, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { AccountData, AccountService, PassOrderPageData } from '../../../api';
import { UseAuthPermissions } from '../../../session/UseAuthPermissions';
import ConfirmationDialog from '../../../common/ui/ConfimationDialog';
import { useLanguage } from '../../../common/GeneralUtils';
import { useSaveHeader } from '../../../experienceSettings/saveHeader/SaveHeaderContext';

type PassDeletionSectionProps = {
  passOrderPage: PassOrderPageData;
  updateLocalAccount: KeyedMutator<AccountData>;
  account: AccountData;
};

export const PassDeletionSection = ({
  passOrderPage,
  updateLocalAccount,
  account: _account,
}: PassDeletionSectionProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { readOnly } = UseAuthPermissions();
  const posthog = usePostHog();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const language = useLanguage();
  const { showSuccess, showError } = useSaveHeader();

  const handleDelete = async () => {
    try {
      posthog.capture('Editing pass settings', { section: 'DeletionSection', passOrderPageId: passOrderPage.id });
      const updatedAccount = await AccountService.archivePassOrderPage(passOrderPage.id);
      await updateLocalAccount(updatedAccount);
      navigate('/dashboard/pass-order-pages');
      showSuccess();
      setIsDialogOpen(false);
    } catch (e) {
      showError((e as Error)?.message || t('saveHeader.errorGeneric'));
    }
  };

  return (
    <Grid container flexDirection="column" alignContent="center" alignItems="center" gap={4}>
      <Typography variant="h6">{t('passes.delete_title')}</Typography>
      <Grid item>
        <Button variant="contained" color="error" onClick={() => setIsDialogOpen(true)} disabled={readOnly}>
          {t('passes.delete_button', { name: passOrderPage.label[language] })}
        </Button>
      </Grid>
      <ConfirmationDialog
        isOpen={isDialogOpen}
        onClose={(actionConfirmed) => {
          if (actionConfirmed) {
            void handleDelete();
          }
          setIsDialogOpen(false);
        }}
        title={t('passes.delete_confirm_title')}
        content={<Typography>{t('passes.delete_confirm_text', { name: passOrderPage.label[language] })}</Typography>}
        ctaText={t('passes.delete_confirm')}
        confirmButtonColor="error"
      />
    </Grid>
  );
};
